function userAgentString(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent;
}

function clientHintBrands(): string {
  const uaData = (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } }).userAgentData;
  return (uaData?.brands ?? []).map((b) => b.brand).join(' ');
}

const NON_CHROME_CHROMIUM = /Edg|EdgA|OPR|OPT|SamsungBrowser|Brave|YaBrowser|UCBrowser|MiuiBrowser|HuaweiBrowser|DuckDuckGo|Vivaldi|Firefox/i;

/** Display modes used by an installed PWA / TWA / desktop app window. */
export const INSTALLED_PWA_DISPLAY_MODES = [
  'standalone',
  'window-controls-overlay',
  'fullscreen',
  'minimal-ui',
] as const;

/** Google Chrome on Android (not Samsung Internet, Edge, Opera, or other Chromium forks). */
export function isGoogleChromeOnAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = userAgentString();
  if (!/Android/i.test(ua) || isIosDevice()) return false;
  const brands = clientHintBrands();
  if (/Google Chrome/i.test(brands) && !NON_CHROME_CHROMIUM.test(brands)) return true;
  if (!/Chrome/i.test(ua)) return false;
  return !NON_CHROME_CHROMIUM.test(ua) && !/Samsung Internet/i.test(brands);
}

/**
 * Android browsers other than Chrome may mint a WebAPK that Android 14+ flags with a
 * privacy warning. Users can still confirm that warning and finish install.
 */
export function shouldShowNonChromeInstallHint(): boolean {
  if (typeof navigator === 'undefined' || isRunningAsInstalledPwa()) return false;
  const ua = userAgentString();
  return /Android/i.test(ua) && !isIosDevice() && !isGoogleChromeOnAndroid();
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function isMacOs(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Macintosh|Mac OS X/.test(navigator.userAgent) && !isIosDevice();
}

export function isSafariBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|Chromium|CriOS|Edg|EdgiOS|FxiOS|OPR/.test(ua);
}

/** iOS/iPadOS Safari tab (not Home Screen shell). */
export function isIosSafariBrowserTab(): boolean {
  return isIosDevice() && isSafariBrowser() && !isRunningAsInstalledPwa();
}

/** iOS/iPadOS Safari: no beforeinstallprompt — user must use Share → Add to Home Screen. */
export function isIosSafariInstallEligible(): boolean {
  return isIosSafariBrowserTab();
}

/** macOS Safari: no install button API — Add to Dock via Share (Sonoma+) or Safari File menu. */
export function isMacSafariInstallEligible(): boolean {
  return isMacOs() && isSafariBrowser() && !isRunningAsInstalledPwa();
}

export type ManualPwaInstallKind = 'ios' | 'mac' | 'generic';

/** Immediate platform path when a native install prompt is not available. */
export function detectManualPwaInstallKind(): ManualPwaInstallKind | null {
  if (typeof window === 'undefined' || isRunningAsInstalledPwa()) return null;
  if (isIosSafariInstallEligible()) return 'ios';
  if (isMacSafariInstallEligible()) return 'mac';
  return null;
}

/** Installed PWA / Home Screen / desktop app / TWA — not a normal browser tab. */
export function isRunningAsInstalledPwa(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (iosStandalone) return true;
  if (typeof document !== 'undefined' && document.referrer.startsWith('android-app://')) return true;
  return INSTALLED_PWA_DISPLAY_MODES.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches);
}

export function subscribeInstalledPwaDisplayMode(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const queries = INSTALLED_PWA_DISPLAY_MODES.map((mode) => window.matchMedia(`(display-mode: ${mode})`));
  queries.forEach((mq) => mq.addEventListener('change', onChange));
  window.addEventListener('appinstalled', onChange);
  return () => {
    queries.forEach((mq) => mq.removeEventListener('change', onChange));
    window.removeEventListener('appinstalled', onChange);
  };
}

type RelatedApp = { platform?: string; url?: string; id?: string };

/**
 * True when Chromium reports this PWA (or a related web app) is already installed,
 * even if the current document is a regular browser tab.
 */
export async function isPwaListedAsInstalledRelatedApp(): Promise<boolean> {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & {
    getInstalledRelatedApps?: () => Promise<RelatedApp[]>;
  };
  if (typeof nav.getInstalledRelatedApps !== 'function') return false;
  try {
    const apps = await nav.getInstalledRelatedApps();
    return apps.some((app) => app.platform === 'webapp');
  } catch {
    return false;
  }
}
